<template>
  <PTransparentLoader :loading="loading" class="event-wizard" v-if="loaded">
    <CContainer class="p-0">
      <h1 class="main-header mb-1" v-translate translate-context="event_wizard">Data collection</h1>
      <p v-translate translate-context="event_wizard.subtitle">Create a data collection form to obtain key information
        from event participants</p>
      <CRow class="mb-4 mt-4" v-if="isFormSaved && canDownloadResults">
        <CCol lg="12" class="text-right">
          <CButton color="success" @click="$emit('downloadResults')" v-translate class="mb-3"
                   translate-context="event_wizard">Download results
          </CButton>
        </CCol>
      </CRow>
      <!-- Form Enable/Disable Section -->
      <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Form Status')"
             :help-text="$pgettext('form_configurator.help', 'Enable or disable the form. When disabled, users will not see the form and cannot submit responses. You can prepare the form configuration and enable it later when ready to collect data')"
             separator
             label-class="big">
        <CRow>
          <CCol>
            <div class="d-flex align-items-center">
              <label class="m-0 mr-2" v-translate translate-context="form_configurator">Form is active</label>
              <CSwitch
                  :checked.sync="form.enable"
                  variant="3d"
                  color="primary"
                  size="sm"
                  @update:checked="previewFieldUpdated"
              />
            </div>
            <div class="mt-2">
              <PAlert :color="form.enable ? 'primary' : 'warning'" :icon="form.enable ? 'cipInfo' : 'cipAlertTriangle'">
                <span v-if="form.enable" v-translate translate-context="form_configurator">
                  Form is enabled - users can see and submit responses to this form
                </span>
                <span v-else v-translate translate-context="form_configurator">
                  Form is disabled - users will not see this form and cannot submit responses
                </span>
              </PAlert>
            </div>
          </CCol>
        </CRow>
      </PFRow>

      <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Title')"
             :help-text="$pgettext('form_configurator.help', 'Insert the form title')"
             separator
             label-class="big">
        <PTranslatableTextInput
            v-model="form.title"
            :placeholder="$pgettext('form_configurator.placeholder','eg: Stay updated on news')"
            :langs="langs"
            @input="previewFieldUpdated"
        />
      </PFRow>
      <PFRow size="5x7" :label="$pgettext('event_wizard.label', 'Logo')"
             :help-text="$pgettext('form_configurator.logo_help','A logo adds a personal touch to your form, creating a more engaging and authentic experience for other participants')"
             separator label-class="big">
        <PUploader
            :params="uploadParams"
            :get_intent="get_logo_upload_intent"
            :value.sync="form.logo"
            :preview="logo_url"
            :help_text="$pgettext('form_configurator.logo_help','Allowed formats: jpg, jpeg, png')"
            @uploaded="newLogoUploaded"
        />
      </PFRow>

      <template v-if="advanced">
        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Primary Color')" separator
               label-class="big">
          <PColorPicker native :color="form.style.primary_color" disable-alpha @change="onChangePrimaryColor"/>
        </PFRow>
        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Button Color')"
               :help-text="$pgettext('form_configurator.help', 'Choose a color for the buttons. If not set, the primary color will be used')"
               separator
               label-class="big">
          <PColorPicker native :color="form.style.button_color || form.style.primary_color" disable-alpha @change="onChangeButtonColor"/>
        </PFRow>
        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Title Color')" separator
               label-class="big">
          <PColorPicker native :color="form.style.title_color" disable-alpha @change="onChangeTitleColor"/>
        </PFRow>
        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Background Color')" separator
               label-class="big">
          <PColorPicker native :color="form.style.bg_color" disable-alpha @change="onChangeBgColor"/>
        </PFRow>

        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Edge curvature')" separator
               label-class="big">
          <div class="btn-group-squared btn-group-toggle">
            <label class="btn btn-outline-secondary btn-toggle" v-for="(radius, index) in elementRadiusOptions"
                   :key="index"
                   :class="{'active': radius.value === form.style.input_radius}">&nbsp;
              <input type="radio" name="form-radius" :id="`option-${index}`"
                     v-model="form.style.input_radius"
                     :value="radius.value"
                     :checked="radius.value === form.style.input_radius"
                     @change="previewFieldUpdated">
              <CIcon size="lg" :name="radius.iconName"/>
            </label>
          </div>
        </PFRow>

        <PFRow v-if="canUseCustomFont" size="5x7" :label="$pgettext('form_configurator.label', 'Font')"
               :help-text="$pgettext('form_configurator.help', 'Choose a font for your form')"
               separator
               label-class="big">
          <FontSelector
              v-model="form.style.font"
              :get-font-upload-intent="get_font_upload_intent"
              :custom-font-preview-url="custom_font_url"
              @change="onFontChange"
              @fontUploaded="onCustomFontUploaded"
          />
        </PFRow>

        <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Actions')"
               :help-text="$pgettext('form_configurator.subtitle', 'Choose from various options to interact with the form')"
               separator
               label-class="big">
          <div class="form-check form-check-inline" v-for="(option, index) in skipOptions" :key="index">
            <label class="form-check-label d-flex flex-column align-items-center cursor-pointer mr-3">
              <span class="mb-3">
               <template v-if="option.input">
                <img :src="require(`@/assets/images/event_wizard/step_form/${option.input}.png`)" style="width: 190px">
              </template>
              <template v-else>
                <span class="d-block" style="padding: 40px 0">{{ option.label }}</span>
              </template>
              </span>
              <input type="radio" name="skip-option" :id="`option-${index}`"
                     class="form-check-input"
                     v-model="form.skip_mode"
                     :value="option.input"
                     :checked="option.input === form.skip_mode"
                     @change="previewFieldUpdated">


            </label>
          </div>
        </PFRow>

      </template>
      <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Fields')"
             :help-text="$pgettext('form_configurator.help', 'Select the data or information you want to ask the user before they access the photos')"
             separator label-class="big">
        <Fields
            :data.sync="form.fields"
            :advanced="advanced"
            :langs="langs"
            @preview="previewFieldUpdated"
        />
      </PFRow>
      <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Privacy and Marketing')"
             :help-text="$pgettext('form_configurator.help', 'Activate privacy and marketing consents')"
             separator
             label-class="big">
        <Consents
            :data.sync="form.consents"
            :get_consent_upload_intent="get_consent_upload_intent"
            :advanced="advanced"
            :langs="langs"
            @preview="previewFieldUpdated"
        />
      </PFRow>

      <!-- Data Storage Section -->
      <PFRow size="5x7" :label="$pgettext('form_configurator.label', 'Data Storage')"
             :help-text="$pgettext('form_configurator.help', 'Choose where to store collected form data')"
             separator
             label-class="big">
        <DataStorage
            :data.sync="form.data_storage"
            :form-fields="form.fields"
            :form-consents="form.consents"
            :pica-service-id="eventId"
            @preview="previewFieldUpdated"
        />
      </PFRow>

      <!-- Custom CSS Section (at the bottom, rarely used) -->
      <PFRow v-if="canUseAdvancedStyling" size="5x7"
             :label="$pgettext('form_configurator.label', 'Custom CSS')"
             :help-text="$pgettext('form_configurator.help', 'Add custom CSS styles to further customize your form appearance')"
             separator
             label-class="big">
        <CssEditor
            v-model="form.style.custom_css"
            @change="previewFieldUpdated"
        />
      </PFRow>
    </CContainer>

    <div class="event-wizard__footer">
      <CRow>
        <CCol>
          <div class="d-flex justify-content-end">
            <div class="mr-2">
              <CButton color="secondary" block @click="onReset" ref="reset-button"
                       v-translate translate-context="event_wizard">
                Reset
              </CButton>
            </div>
            <div>
              <CButton color="primary" @click="onSave" block :disabled="!isDirty" ref="save-button"
                       v-translate translate-context="event_wizard">
                Save
              </CButton>
            </div>
          </div>
        </CCol>
      </CRow>
    </div>
  </PTransparentLoader>
</template>

<script>
import PUploader from "@/domain/core/components/PUploader.vue";
import Consents from "@/domain/formConfigurator/component/Consents.vue";
import Fields from "@/domain/formConfigurator/component/Fields.vue";
import DataStorage from "@/domain/formConfigurator/component/DataStorage.vue";
import FontSelector from "@/domain/formConfigurator/component/FontSelector.vue";
import CssEditor from "@/domain/formConfigurator/component/CssEditor.vue";
import PTranslatableTextInput from "@/domain/core/components/PTranslatableTextInput.vue";

import {
  defaultConsents,
  defaultFields,
  generateKey,
  generateRandomId,
  skipOptions,
  elementRadiusOptions,
  defaultFieldSize,
} from "@/domain/formConfigurator/fields";
import dataStorageOptions from "@/domain/core/constant/dataStorageOptions";
import {DELETE_KEY} from "@/domain/core/components/uploader";
import {cloneDeep, isEqual} from "lodash";
import PColorPicker from "@/domain/core/components/PColorPicker.vue";
import PTransparentLoader from "@/domain/core/components/PTransparentLoader.vue";
import PFRow from "@/domain/core/components/PFRow.vue";
import {mapGetters, mapActions} from "vuex";
import PAlert from "@/components/PAlert.vue";

const defaultForm = {
  enable: false,
  title: {},
  skip_mode: '',
  logo: '',
  preview_logo: '',
  style: {
    bg_color: '#FFFFFF',
    title_color: '#1867FF',
    primary_color: '#1867FF',
    button_color: '',
    input_radius: 0,
    font: {
      type: 'default',
      google_font: null,
      custom_font_url: null,
      custom_font_name: null,
      scale: 1
    },
    custom_css: '',
  },
  fields: [...defaultFields],
  consents: [...defaultConsents],
  data_storage: {
    provider: dataStorageOptions.default(),
    config: {
      endpoint: '',
      pattern: '',
    }
  },
}


export default {
  name: "StepForm",
  components: {
    PAlert,
    PFRow, PTransparentLoader, PColorPicker, PTranslatableTextInput, Fields, Consents, DataStorage, PUploader, FontSelector, CssEditor
  },
  props: {
    get_consent_upload_intent: {
      type: Function,
      required: true
    },
    get_form_s3_public_url: {
      type: Function,
      required: true
    },
    get_logo_upload_intent: {
      type: Function,
      required: true
    },
    get_upload_intent_public_url: {
      type: Function,
      required: true,
    },
    get_font_upload_intent: {
      type: Function,
      required: false,
    },
    isPublished: {
      type: Boolean,
      required: true
    },
    isPicaManager: {
      type: Boolean,
      required: true
    },
    advanced: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isDirty: false,
      savedSnapshot: null,
      logo_url: null,
      custom_font_url: null,
      loaded: false,
      showFieldEditor: false,
      fieldEdited: {},
      form: {},
    }
  },
  computed: {
    ...mapGetters('eventWizard', [
      'step_form',
      'isStepLoading',
      'eventId'
    ]),
    loading() {
      return this.isStepLoading('form')
    },
    langs() {
      return this.advanced ? process.env.VUE_APP_SUPPORTED_LANGS.split(',') : ['it']
    },
    skipOptions() {
      return skipOptions
    },
    elementRadiusOptions() {
      return elementRadiusOptions
    },
    uploadParams() {
      return {
        allowed_extensions: ['jpg', 'jpeg', 'png'],
        compression: {minWidth: 600, height: 600},
        input_image_size: {maxWidth: 2000},
      }
    },
    isFormSaved() {
      return this.step_form && this.step_form.form && 'id' in this.step_form.form
    },
    canDownloadResults() {
      return this.form.data_storage?.provider === dataStorageOptions.PICA_STORAGE
    },
    canUseAdvancedStyling() {
      return this.hasPerm('event_wizard.form.advanced_styling')
    },
    canUseCustomFont() {
      return this.hasPerm('event_wizard.form.custom_font')
    },
  },
  emits: ['save', 'updatePreview', 'downloadResults'],
  watch: {
    form: {
      deep: true,
      handler() {
        this.checkDirty()
      }
    }
  },
  async mounted() {
    if (!this.step_form)
      await this.fetchStepData('form')
    this.onReset()
  },
  methods: {
    ...mapActions('eventWizard', [
      'fetchStepData'
    ]),
    saveSnapshot() {
      this.savedSnapshot = cloneDeep(this.form)
      this.isDirty = false
    },
    checkDirty() {
      if (!this.savedSnapshot) return
      this.isDirty = !isEqual(this.form, this.savedSnapshot)
    },
    onChangeBgColor(color) {
      this.form.style.bg_color = color.hex
      this.previewFieldUpdated()
    },
    onChangePrimaryColor(color) {
      this.form.style.primary_color = color.hex
      this.previewFieldUpdated()
    },
    onChangeButtonColor(color) {
      this.form.style.button_color = color.hex
      this.previewFieldUpdated()
    },
    onChangeTitleColor(color) {
      this.form.style.title_color = color.hex
      this.previewFieldUpdated()
    },

    async onReset() {
      let formData = this.step_form && this.step_form.form
      if (!formData || !formData.fields)
        formData = defaultForm

      this.logo_url = await this.get_form_s3_public_url(formData.logo)
      // Load custom font URL if present (stored as S3 path after save, like logo)
      if (formData.style?.font?.custom_font_url) {
        this.custom_font_url = await this.get_form_s3_public_url(formData.style.font.custom_font_url)
      } else {
        this.custom_font_url = null
      }
      if (!formData.fields) formData.fields = []
      if (!formData.consents) formData.consents = []

      this.form = {
        enable: formData.enable ?? false,
        title: {...formData.title},
        style: {
          ...defaultForm.style,
          ...formData.style,
          font: formData.style?.font ?? {...defaultForm.style.font}
        },
        skip_mode: formData.skip_mode,
        logo: formData.logo,
        data_storage: formData.data_storage ? {...formData.data_storage} : {
          provider: dataStorageOptions.default(),
          config: {
            endpoint: '',
            pattern: ''
          }
        },
        fields: formData.fields.map(field => {
          return {
            id: generateRandomId(),
            type: field.type,
            order: field.order,
            mandatory: field.mandatory,
            editable: this.isFieldEditable(field),
            deletable: this.isFieldDeletable(field),
            key: field.key,
            name: field.name,
            size: field.size ?? defaultFieldSize,
            params: field.params ?? {}
          }
        }),
        consents: formData.consents.map(consent => {
          return {
            order: consent.order,
            id: generateRandomId(),
            mandatory: consent.mandatory,
            editable: this.isConsentEditable(consent),
            deletable: this.isConsentDeletable(consent),
            key: consent.key,
            text: consent.text,
            uploads: [],
          }
        }),
      }

      await this.previewFieldUpdated()
      this.loaded = true
      this.$nextTick(() => this.saveSnapshot())
    },
    async onSave() {
      await this.emitPromised('save', {form: this.getMappedForm()})
      await this.$nextTick()
      this.saveSnapshot()
    },
    async previewFieldUpdated() {
      this.$emit('updatePreview', await this.getPreviewForm())
      this.$nextTick(() => this.checkDirty())
    },
    getMappedForm() {
      return {
        enable: this.form.enable,
        title: {...this.form.title},
        style: {...this.form.style},
        skip_mode: this.form.skip_mode,
        logo: this.form.logo,
        data_storage: {...this.form.data_storage},
        fields: this.form.fields.map(field => {
          if (!field.key || field.key.length <= 0)
            field.key = generateKey(field.name)
          return {
            type: field.type,
            order: field.order,
            mandatory: field.mandatory,
            key: field.key,
            name: field.name,
            size: field.size,
            params: field.params ?? {}
          }
        }),
        consents: this.form.consents.map(consent => {
          return {
            order: consent.order,
            key: consent.key,
            mandatory: consent.mandatory,
            uploads: consent.uploads.filter(upload => upload.intent).map(upload => upload.intent),
            text: consent.text ?? {}
          }
        }),
      }
    },
    async getPreviewForm() {
      const previewForm = cloneDeep(this.getMappedForm())
      previewForm.logo = this.logo_url
      // Replace font URL with presigned URL for preview, or clear it if deleted
      if (previewForm.style?.font) {
        // Use presigned URL if available, otherwise clear to prevent S3 path leak
        previewForm.style.font.custom_font_url = this.custom_font_url || null
      }
      previewForm.consents = await Promise.all(previewForm.consents.map(async consent => {
        for (const lang in consent.text) {
          consent.text[lang] = await this.replaceS3Link(consent.text[lang])
          consent.text[lang] = await this.replaceIntentLink(consent.text[lang])
        }
        return consent
      }))
      return previewForm
    },

    async replaceS3Link(text) {
      const promises = [];
      const regex = /{{S3_FILE:(.*?)}}/g;
      text.replace(regex, (match, file) => {
        const promise = this.get_form_s3_public_url(file);
        promises.push(promise);
      });
      const data = await Promise.all(promises);
      return text.replace(regex, () => data.shift());
    },
    async replaceIntentLink(text) {
      const promises = [];
      const regex = /{{INTENT:(.*?)}}/g;
      text.replace(regex, (match, file) => {
        const promise = this.get_upload_intent_public_url(file)
        promises.push(promise);
      });
      const data = await Promise.all(promises);
      return text.replace(regex, () => data.shift());
    },

    isFieldEditable(field) {
      if (this.isPicaManager) return true // Pica operator can always edit fields
      if (this.isPublished) return false // If it's published, we don't want to have mismatching fields in the export
      return !(field.type === 'email' && field.name.it === 'Email'); // Email field is never editable
    },
    isFieldDeletable(field) {
      if (this.isPicaManager) return true // Pica operator can always edit fields
      return !(field.type === 'email' && field.name.it === 'Email'); // Email field is never editable
    },
    // eslint-disable-next-line no-unused-vars
    isConsentEditable(consent) {
      if (this.isPicaManager) return true // Pica operator can always edit fields
      if (this.isPublished) return false // If it's published, we don't want to have mismatching fields in the export
      return true
    },
    // eslint-disable-next-line no-unused-vars
    isConsentDeletable(consent) {
      if (this.isPicaManager) return true // Pica operator can always edit fields
      return true
    }, async newLogoUploaded() {
      await this.$nextTick()
      if (this.form.logo === DELETE_KEY)
        this.logo_url = ''
      else {
        this.logo_url = await this.get_upload_intent_public_url(this.form.logo)
      }
      this.previewFieldUpdated()
    },
    onFontChange() {
      // Clear custom_font_url if font type is not custom or if deleted
      const fontType = this.form.style.font?.type
      if (fontType !== 'custom') {
        this.custom_font_url = null
      }
      this.previewFieldUpdated()
    },
    async onCustomFontUploaded() {
      await this.$nextTick()
      if (this.form.style.font?.custom_font_url === DELETE_KEY) {
        this.custom_font_url = null
        this.form.style.font.custom_font_name = null
      } else if (this.form.style.font?.custom_font_url) {
        this.custom_font_url = await this.get_upload_intent_public_url(this.form.style.font.custom_font_url)
        // Generate font name for FontFace
        this.form.style.font.custom_font_name = `CustomFont_${Date.now()}`
      }
      this.previewFieldUpdated()
    },
  }
}
</script>

<style scoped>

</style>
